import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, Info, Target } from 'lucide-react';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';

/**
 * Agreement between the AI's routing and what a human dispatcher said it
 * should have been. This is the number the AppDirect engagement gates on.
 *
 * Two display rules here are correctness requirements, not preferences:
 *
 * 1. **A null rate is "not yet measured", never 0%.** The API returns null
 *    whenever the denominator is zero. "0% agreement" and "nobody has labelled
 *    anything yet" are opposite claims, and showing the first when the second
 *    is true would tell a customer the AI is failing when it has simply not
 *    been graded.
 *
 * 2. **Owner agreement is not shown as a percentage unless the API says it is
 *    measurable.** Only four of the fifteen departments have seeded teams, so
 *    most decisions carry `owner: null`; the rosters that do exist are partly
 *    invented, and the live dev roster contains a name that appears nowhere in
 *    the repo (docs/dispatch/BASELINE_2026-09-02.md §N6/§N7,
 *    docs/dispatch/README.md §7). An owner percentage computed on top of that
 *    would be a real-looking number with nothing behind it. We show the API's
 *    reason instead, and fail closed: anything other than an explicit
 *    `measurable: true` suppresses the number.
 *
 * Owner labels are still captured — see LabelPanel — so the column can be
 * scored later once the rosters are real.
 */

// The engagement's acceptance threshold. Humans themselves run ~85-90%.
const TARGET_RATE = 0.9;

/**
 * `rate` is a 0.0-1.0 fraction — `_rate()` in backend/routes/ai_dispatcher.py
 * returns `round(agree / total, 4)`. The `> 1` branch is a guard against that
 * ever being changed to a pre-multiplied percentage, which would otherwise
 * render 92% as 9200%. The two readings only collide below 1% agreement, which
 * is not a state this screen will be in.
 */
function toPercent(rate) {
  if (rate == null || Number.isNaN(rate)) return null;
  return rate > 1 ? rate : rate * 100;
}

function rateClass(rate) {
  const pct = toPercent(rate);
  if (pct == null) return 'text-boss-text-muted';
  if (pct >= TARGET_RATE * 100) return 'text-[var(--boss-success-text)]';
  if (pct >= 80) return 'text-[var(--boss-warning-text)]';
  return 'text-[var(--boss-danger-text)]';
}

function formatRate(rate) {
  const pct = toPercent(rate);
  return pct == null ? null : `${Math.round(pct)}%`;
}

function NotMeasured({ children = 'Not yet measured' }) {
  return <span className="text-sm text-boss-text-muted italic">{children}</span>;
}

function RateTile({ label, stat }) {
  const agree = stat?.agree ?? 0;
  const disagree = stat?.disagree ?? 0;
  const text = formatRate(stat?.rate);
  const pct = toPercent(stat?.rate);

  return (
    <div className="bg-boss-elevated border border-boss-border rounded-lg p-3">
      <p className="text-xs text-boss-text-secondary">{label}</p>
      {text ? (
        <>
          <p className={`text-2xl font-semibold mt-1 ${rateClass(stat.rate)}`}>{text}</p>
          <p className="text-[11px] text-boss-text-muted mt-0.5">
            {agree} agreed · {disagree} disagreed
          </p>
          <p className="text-[11px] text-boss-text-muted mt-1 flex items-center gap-1">
            <Target size={11} className="shrink-0" />
            {pct >= TARGET_RATE * 100 ? 'At or above the 90% target' : 'Below the 90% target'}
          </p>
        </>
      ) : (
        <>
          <p className="mt-1">
            <NotMeasured />
          </p>
          <p className="text-[11px] text-boss-text-muted mt-0.5">
            Nothing labelled in this view yet
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Owner deliberately does not use RateTile — it must not be able to render a
 * bare percentage by accident.
 *
 * Belt and braces: the backend also nulls `owner.rate` whenever `measurable`
 * is false, so `rate ?? '—'` alone would already be safe. This still gates on
 * `measurable === true` rather than trusting that, because the guarantee we
 * care about is "no meaningless owner percentage reaches a customer", and it
 * should not depend on both sides staying in agreement. `agree` / `disagree`
 * counts stay populated either way; only the percentage is withheld.
 */
function OwnerTile({ owner }) {
  const measurable = owner?.measurable === true;
  const text = formatRate(owner?.rate);
  const note = owner?.note;

  return (
    <div className="bg-boss-elevated border border-boss-border rounded-lg p-3">
      <p className="text-xs text-boss-text-secondary">Owner</p>
      {measurable && text ? (
        <>
          <p className={`text-2xl font-semibold mt-1 ${rateClass(owner.rate)}`}>{text}</p>
          <p className="text-[11px] text-boss-text-muted mt-0.5">
            {owner.agree ?? 0} agreed · {owner.disagree ?? 0} disagreed
          </p>
        </>
      ) : (
        <>
          <p className="mt-1">
            <NotMeasured>Not scored</NotMeasured>
          </p>
          <p className="text-[11px] text-boss-text-muted mt-1 leading-relaxed">
            {note ||
              'Most departments have no team seeded, so the AI has no owner to pick. ' +
                'Owner answers are still being recorded for later.'}
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Values the labeller recorded that our classifier has no way to emit.
 *
 * This sits next to the rate because neither number means much alone. A high
 * agreement rate beside a high off-enum count means the labeller kept picking
 * from our list because ours was the list on screen; a low rate beside a high
 * count means the enum is the bug, not the prompt. Absent entirely — an older
 * backend, or a range with none — the section simply does not render.
 */
function OffEnumBreakdown({ offEnum }) {
  if (!offEnum) return null;
  const departments = Array.isArray(offEnum.departments) ? offEnum.departments : [];
  const ticketTypes = Array.isArray(offEnum.ticket_types) ? offEnum.ticket_types : [];
  if (departments.length === 0 && ticketTypes.length === 0) return null;

  const rows = [
    { heading: 'Departments', values: departments },
    { heading: 'Ticket types', values: ticketTypes },
  ].filter((r) => r.values.length > 0);

  return (
    <div className="boss-alert-warning rounded-lg p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-boss-text">
            Ground truth our taxonomy cannot express
          </p>
          <p className="text-[11px] text-boss-text-secondary mt-0.5">
            {(offEnum.labeled_rows ?? 0).toLocaleString()} labelled{' '}
            {offEnum.labeled_rows === 1 ? 'decision names' : 'decisions name'} a value the AI has
            no option for. These are not model errors — it could not have produced them.
          </p>

          {rows.map((row) => (
            <div key={row.heading} className="mt-2">
              <p className="text-[11px] text-boss-text-secondary uppercase tracking-wide">
                {row.heading}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {row.values.map((v) => (
                  <span
                    key={v.value}
                    className="boss-chip boss-chip-neutral max-w-full"
                    title={`${v.value} — ${v.count} labelled`}
                  >
                    <span className="truncate">{v.value}</span>
                    <span className="text-boss-text-muted">{v.count}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AgreementSummary({ distro, department, fromDate, toDate, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAgreement = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (distro) params.set('distro', distro);
      if (department) params.set('department', department);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);

      const qs = params.toString();
      const res = await api.get(
        `${API_URL}/api/ai-dispatcher/decisions/agreement${qs ? `?${qs}` : ''}`,
      );
      const body = await res.json();
      if (!res.ok) {
        const detail = typeof body?.detail === 'string' ? body.detail : null;
        throw new Error(detail || 'Failed to load agreement');
      }
      setData(body);
    } catch (err) {
      setError(err?.message || 'Failed to load agreement');
    } finally {
      setLoading(false);
    }
  }, [distro, department, fromDate, toDate]);

  useEffect(() => {
    fetchAgreement();
  }, [fetchAgreement, refreshKey]);

  if (loading && !data) {
    return (
      <div className="glass-card p-4">
        <div className="h-4 w-40 rounded bg-boss-elevated animate-pulse" />
        <div className="h-16 mt-3 rounded bg-boss-elevated animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="boss-alert-danger rounded-xl p-4 flex items-start gap-3">
        <AlertCircle size={18} className="shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-boss-text">Agreement summary unavailable — {error}</p>
          <button
            onClick={fetchAgreement}
            className="text-xs text-violet-400 hover:text-violet-300 mt-1"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const labeled = data.labeled_count ?? 0;
  const total = data.total_count ?? 0;
  const coverage = total > 0 ? Math.min(100, (labeled / total) * 100) : 0;
  const byDepartment = Array.isArray(data.by_department) ? data.by_department : [];

  return (
    <div className="glass-card p-4 space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-boss-text">Agreement with human labels</h3>
        <p className="text-xs text-boss-text-secondary">
          <span className="text-boss-text font-medium">{labeled.toLocaleString()}</span> of{' '}
          {total.toLocaleString()} decisions labelled
        </p>
      </div>

      <div>
        <div
          className="h-1.5 w-full rounded-full bg-boss-elevated overflow-hidden"
          role="progressbar"
          aria-valuenow={Math.round(coverage)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Share of decisions that have been labelled"
        >
          <div className="h-full bg-violet-500 transition-all" style={{ width: `${coverage}%` }} />
        </div>
      </div>

      {labeled === 0 && (
        <div className="boss-alert-warning rounded-lg p-3 flex items-start gap-2">
          <Info size={16} className="shrink-0 mt-0.5" />
          <p className="text-xs text-boss-text">
            Nothing has been labelled in this view yet, so there is no agreement rate to report.
            Open a decision and record what it should have been to start the count.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <RateTile label="Department" stat={data.department} />
        <RateTile label="Ticket type" stat={data.ticket_type} />
        <OwnerTile owner={data.owner} />
      </div>

      <OffEnumBreakdown offEnum={data.off_enum} />

      {byDepartment.length > 0 && (
        // pt-4 stacks on the parent's space-y-4 margin (32px total) to set the
        // table apart from the tiles / off-enum box above. Padding, not mt-*:
        // space-y's margin rule is more specific and would override an mt-*.
        <div className="pt-4">
          <h4 className="text-xs font-semibold text-boss-text-secondary uppercase tracking-wider mb-2">
            By department
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-boss-border">
                  <th className="text-left px-2 py-2 text-boss-text-secondary font-medium">
                    Department
                  </th>
                  <th className="text-right px-2 py-2 text-boss-text-secondary font-medium whitespace-nowrap">
                    Labelled
                  </th>
                  <th className="text-right px-2 py-2 text-boss-text-secondary font-medium whitespace-nowrap">
                    Dept rate
                  </th>
                  <th className="text-right px-2 py-2 text-boss-text-secondary font-medium whitespace-nowrap">
                    Type rate
                  </th>
                </tr>
              </thead>
              <tbody>
                {byDepartment.map((row) => (
                  <tr key={row.department ?? '—'} className="border-b border-boss-border last:border-0">
                    <td className="px-2 py-1.5 text-boss-text">
                      {row.department || <span className="text-boss-text-muted">No department</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right text-boss-text-secondary">
                      {(row.labeled ?? 0).toLocaleString()}
                    </td>
                    <td className={`px-2 py-1.5 text-right font-medium ${rateClass(row.dept_rate)}`}>
                      {formatRate(row.dept_rate) ?? (
                        <span title="Not yet measured — nothing labelled for this department">—</span>
                      )}
                    </td>
                    <td className={`px-2 py-1.5 text-right font-medium ${rateClass(row.type_rate)}`}>
                      {formatRate(row.type_rate) ?? (
                        <span title="Not yet measured — nothing labelled for this department">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-boss-text-muted mt-2">
            A dash means not yet measured — no labels in that department. It does not mean zero
            agreement.
          </p>
        </div>
      )}
    </div>
  );
}
